import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { useCollectionArtworks } from '../hooks/useCollectionArtworks';
import { useCollectionStats } from '../hooks/useCollectionStats';
import { supabase } from '../../../../../utils/supabaseClient';

export default function Artworks() {
  const { collectionId } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  const show = params.get('show') || 'all';  // Default to 'all' to show everything
  const sort = params.get('sort') || 'display';
  const view = params.get('view') || 'list';
  const q = params.get('q') || '';
  const page = parseInt(params.get('page') || '1', 10);
  const pageSize = parseInt(params.get('size') || '24', 10);

  const { items, total, loading, error } = useCollectionArtworks(collectionId, {
    show,
    sort,
    page,
    pageSize,
  });
  const { artworks_total, artworks_accepted, artists_total } = useCollectionStats(collectionId);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((m) => {
      const title = (m.title || '').toLowerCase();
      const artist = (m.artist?.nickname || `${m.artist?.first_name || ''} ${m.artist?.last_name || ''}`.trim()).toLowerCase();
      return title.includes(needle) || artist.includes(needle);
    });
  }, [items, q]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === '') next.delete(key);
    else next.set(key, String(value));
    if (key !== 'page') next.set('page', '1'); // reset page when changing filters/sort
    setParams(next, { replace: true });
  };

  // Actions
  const copyLink = (vcId) => {
    const url = `${window.location.origin}/artworks/${vcId}`;
    navigator.clipboard?.writeText(url);
    setOpenMenu(null);
  };
  const openPublic = (vcId) => {
    const url = `/artworks/${vcId}`;
    window.open(url, '_blank', 'noopener');
    setOpenMenu(null);
  };
  const editArtwork = (vcId) => {
    navigate(`/admin/artworks/${vcId}/edit`);
    setOpenMenu(null);
  };
  const refreshItem = () => {
    // Trigger a soft refresh by toggling a no-op param
    updateParam('t', Date.now());
    setOpenMenu(null);
  };

  // Close menus on outside click and ESC; focus management
  useEffect(() => {
    const onDocClick = (e) => {
      if (!openMenu) return;
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      const inToggle = toggleRef.current && toggleRef.current.contains(e.target);
      if (!inMenu && !inToggle) setOpenMenu(null);
    };
    const onKey = (e) => {
      if (!openMenu) return;
      if (e.key === 'Escape') {
        setOpenMenu(null);
        // Restore focus to toggle
        if (toggleRef.current) toggleRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  useEffect(() => {
    if (openMenu && menuRef.current) {
      // Focus first item when menu opens
      const first = menuRef.current.querySelector('button');
      if (first) first.focus();
    }
  }, [openMenu]);

  const onToggleKeyDown = (e, vcId) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpenMenu((curr) => (curr === vcId ? null : vcId));
    }
  };

  const onMenuKeyDown = (e) => {
    const items = menuRef.current ? Array.from(menuRef.current.querySelectorAll('button')) : [];
    const idx = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[(idx + 1) % items.length] || items[0];
      next && next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[(idx - 1 + items.length) % items.length] || items[items.length - 1];
      prev && prev.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0] && items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1] && items[items.length - 1].focus();
    }
  };

  const copyImageUrl = (url) => {
    if (!url) return;
    navigator.clipboard?.writeText(url);
    setOpenMenu(null);
  };

  const openInAdmin = (vcId) => {
    window.open(`/admin/artworks/${vcId}`, '_blank', 'noopener');
    setOpenMenu(null);
  };

  const toggleAcceptance = async (vcId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const { error } = await supabase
        .from('collection_vc_mapping')
        .update({ accepted: newStatus })
        .eq('collection_id', collectionId)
        .eq('vc_id', vcId);

      if (error) throw error;

      // Trigger refresh
      updateParam('t', Date.now());
    } catch (err) {
      console.error('Error toggling acceptance:', err);
      alert('Failed to update acceptance status');
    }
  };

  const removeFromCollection = async (vcId, title) => {
    const confirmMessage = `Are you sure you want to remove "${title}" from this collection?\n\nThis will delete the artwork from the collection but will NOT delete the artwork itself.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('collection_vc_mapping')
        .delete()
        .eq('collection_id', collectionId)
        .eq('vc_id', vcId);

      if (error) throw error;

      // Trigger refresh
      updateParam('t', Date.now());
      alert('Artwork removed from collection successfully');
    } catch (err) {
      console.error('Error removing artwork:', err);
      alert(`Failed to remove artwork: ${err.message}`);
    }
  };

  return (
    <div className="curator-v2-card">
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 12 }}>
        <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>Artworks: {artworks_total ?? total}</div>
        <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>Accepted: {artworks_accepted ?? '—'}</div>
        <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>Artists: {artists_total ?? '—'}</div>
      </div>

      {/* Toolbar */}
      <div className="toolbar-row" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div className="left" style={{ display: 'flex', gap: 12 }}>
          <div>
            <input
              type="text"
              placeholder="Search artworks, artists"
              value={q}
              onChange={(e) => updateParam('q', e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, minWidth: 220 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Show:</span>
            <select value={show} onChange={(e) => updateParam('show', e.target.value)}>
              <option value="all">All Artworks</option>
              <option value="accepted">Accepted Only</option>
            </select>
          </label>
        </div>
        <div className="right" style={{ display: 'flex', gap: 12 }}>
          <div style={{ display: 'inline-flex', gap: 8, background: '#f3f4f6', padding: 4, borderRadius: 999 }}>
            <button onClick={() => updateParam('view', 'grid')} aria-pressed={view==='grid'} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: view==='grid' ? '#fff' : 'transparent' }}>Grid</button>
            <button onClick={() => updateParam('view', 'list')} aria-pressed={view==='list'} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: view==='list' ? '#fff' : 'transparent' }}>List</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Sort:</span>
            <select value={sort} onChange={(e) => updateParam('sort', e.target.value)}>
              <option value="display">Display order</option>
              <option value="newest">Newest</option>
            </select>
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="content">
        {loading && <p>Loading…</p>}
        {error && <p className="error">{String(error.message || error)}</p>}
        {!loading && !error && (
          <>
            {view === 'grid' ? (
              <div
                className="artworks-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '12px'
                }}
              >
                {filtered.map((m) => (
                  <div key={`${m.vc_id}`} className="artwork-card" style={{
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: 12,
                    padding: 10,
                    background: 'var(--card, #fff)'
                  }}>
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#f3f4f6' }}>
                      {m.cover_image ? (
                        <img
                          src={m.cover_image}
                          alt={m.title || `Artwork ${m.vc_id}`}
                          style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <div className="thumb" style={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          fontSize: 12
                        }}>
                          No image
                        </div>
                      )}
                      {/* vc_id pill */}
                      <div style={{ position: 'absolute', top: 8, left: 8, background: '#7c3aed', color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                        #{m.vc_id}
                      </div>
                      {/* accepted check */}
                      {m.accepted && (
                        <div style={{ position: 'absolute', top: 8, right: 8, background: '#10b981', color: '#fff', borderRadius: '999px', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>✓</div>
                      )}
                    </div>
                    <div className="meta" style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.2 }}>{m.title || 'Untitled'}</div>
                        <div style={{ position: 'relative' }}>
                          <button
                            ref={openMenu === m.vc_id ? toggleRef : null}
                            onClick={() => setOpenMenu(openMenu === m.vc_id ? null : m.vc_id)}
                            onKeyDown={(e) => onToggleKeyDown(e, m.vc_id)}
                            aria-haspopup="menu"
                            aria-expanded={openMenu === m.vc_id}
                            aria-label="Artwork actions"
                            style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}
                          >
                            ⋯
                          </button>
                          {openMenu === m.vc_id && (
                            <div
                              ref={menuRef}
                              role="menu"
                              tabIndex={-1}
                              onKeyDown={onMenuKeyDown}
                              style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, minWidth: 180, boxShadow: '0 4px 10px rgba(0,0,0,0.06)', zIndex: 10 }}
                            >
                              <button onClick={() => editArtwork(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>✏️ Edit</button>
                              <button onClick={() => openInAdmin(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>🗂 Open in Admin</button>
                              <button onClick={() => copyLink(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>📋 Copy Public Link</button>
                              <button onClick={() => openPublic(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>↗️ Open Public</button>
                              <button disabled={!m.cover_image} onClick={() => copyImageUrl(m.cover_image)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', opacity: m.cover_image ? 1 : 0.5 }}>🖼 Copy Image URL</button>
                              <button onClick={refreshItem} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>🔄 Refresh</button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {m.artist?.nickname || `${m.artist?.first_name || ''} ${m.artist?.last_name || ''}`.trim() || 'Unknown artist'}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {m.display_order != null && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>#{m.display_order}</span>
                        )}
                        {(m.width || m.height) && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>{[m.width && `${m.width}"`, m.height && `${m.height}"`].filter(Boolean).join('×')}</span>
                        )}
                        {m.price != null && (
                          <span style={{ fontSize: 11, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 999, padding: '2px 6px' }}>${m.price}</span>
                        )}
                      </div>
                      {/* Accept/Reject Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAcceptance(m.vc_id, m.accepted);
                        }}
                        style={{
                          marginTop: 8,
                          width: '100%',
                          padding: '8px',
                          borderRadius: 8,
                          border: 'none',
                          background: m.accepted ? '#dcfce7' : '#fef3c7',
                          color: m.accepted ? '#166534' : '#92400e',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                      >
                        {m.accepted ? '✓ Accepted' : 'Review'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="artworks-list" style={{ display: 'grid', gap: 10 }}>
                {filtered.map((m) => (
                  <div key={`${m.vc_id}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr auto', gap: 12, alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 12, padding: 8, background: '#fff' }}>
                    {m.cover_image ? (
                      <img src={m.cover_image} alt={m.title || `Artwork ${m.vc_id}`} style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8 }} />
                    ) : (
                      <div style={{ width: 100, height: 75, background: '#f3f4f6', borderRadius: 8 }} />
                    )}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{m.title || 'Untitled'}</div>
                        <span style={{ fontSize: 11, background: '#ede9fe', color: '#6d28d9', borderRadius: 999, padding: '2px 6px' }}>#{m.vc_id}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{m.artist?.nickname || `${m.artist?.first_name || ''} ${m.artist?.last_name || ''}`.trim() || 'Unknown artist'}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        {(m.width || m.height) && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>{[m.width && `${m.width}"`, m.height && `${m.height}"`].filter(Boolean).join('×')}</span>
                        )}
                        {m.price != null && (
                          <span style={{ fontSize: 11, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 999, padding: '2px 6px' }}>${m.price}</span>
                        )}
                        <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 999, background: m.accepted ? '#ecfdf5' : '#f3f4f6', color: m.accepted ? '#065f46' : '#4b5563', border: `1px solid ${m.accepted ? '#a7f3d0' : '#e5e7eb'}` }}>
                          {m.accepted ? 'Accepted' : 'Submitted'}
                        </span>
                        {m.display_order != null && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>#{m.display_order}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button
                        ref={openMenu === m.vc_id ? toggleRef : null}
                        onClick={() => setOpenMenu(openMenu === m.vc_id ? null : m.vc_id)}
                        onKeyDown={(e) => onToggleKeyDown(e, m.vc_id)}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}
                        aria-haspopup="menu"
                        aria-expanded={openMenu === m.vc_id}
                      >
                        Actions ▾
                      </button>
                      {openMenu === m.vc_id && (
                        <div
                          ref={menuRef}
                          role="menu"
                          tabIndex={-1}
                          onKeyDown={onMenuKeyDown}
                          style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, minWidth: 180, boxShadow: '0 4px 10px rgba(0,0,0,0.06)', zIndex: 10 }}
                        >
                          <button onClick={() => { toggleAcceptance(m.vc_id, m.accepted); setOpenMenu(null); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: m.accepted ? '#dcfce7' : '#fef3c7', color: m.accepted ? '#166534' : '#92400e', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{m.accepted ? '✓ Accepted' : '○ Review'}</button>
                          <button onClick={() => editArtwork(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>✏️ Edit</button>
                          <button onClick={() => openInAdmin(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>🗂 Open in Admin</button>
                          <button onClick={() => copyLink(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>📋 Copy Public Link</button>
                          <button onClick={() => openPublic(m.vc_id)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>↗️ Open Public</button>
                          <button disabled={!m.cover_image} onClick={() => copyImageUrl(m.cover_image)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', opacity: m.cover_image ? 1 : 0.5 }}>🖼 Copy Image URL</button>
                          <button onClick={refreshItem} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>🔄 Refresh</button>
                          <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                          <button onClick={() => { removeFromCollection(m.vc_id, m.title); setOpenMenu(null); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>🗑️ Remove from Collection</button>
                          <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                          <button onClick={() => { removeFromCollection(m.vc_id, m.title); setOpenMenu(null); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>🗑️ Remove from Collection</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={page <= 1} onClick={() => updateParam('page', Math.max(1, page - 1))}>Prev</button>
                <button disabled={page * pageSize >= total} onClick={() => updateParam('page', page + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
